import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { 
  ArrowRight, 
  Building, 
  Award, 
  Users, 
  Target,
  CheckCircle,
  Lightbulb,
  Heart
} from 'lucide-react';

const ABOUT_IMAGE = "https://customer-assets.emergentagent.com/job_d8aebd02-1a2f-47f4-a1b5-34ff2bb8d378/artifacts/jr0zfrs3_David%20Ashley%20Workshop%20Pic.jpg";

const values = [
  { icon: Target, title: 'Strategy', description: 'Clear, actionable plans that turn vision into reality.' },
  { icon: CheckCircle, title: 'Structure', description: 'Systematic approaches that create sustainable success.' },
  { icon: Heart, title: 'Service', description: 'Genuine commitment to your growth and success.' },
  { icon: Lightbulb, title: 'Solutions', description: 'Practical answers to real business challenges.' },
];

const highlights = [
  'Business Strategist & Consultant',
  'Serial Entrepreneur with 10+ Active Businesses',
  'Assistant Pastor & Ministry Leader',
  'Nonprofit Founder',
  'Workforce Development and Career Strategist',
  'Discipline and Accountability, Health and Wellness Coach',
];

export default function AboutPage() {
  return (
    <Layout title="About" description="Meet David C. Ashley, business strategist helping entrepreneurs build successful businesses.">
      {/* Hero Section */}
      <section className="section-padding bg-white" data-testid="about-hero">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="overline mb-4 inline-block">About David Ashley</span>
              <h1 className="heading-1 text-4xl sm:text-5xl lg:text-6xl text-[#0A0F1C] mb-6" data-testid="about-title">
                Hello, I'm <span className="text-[#1D4ED8]">David Ashley</span>
              </h1>
              <p className="body-text text-[#475569] text-lg mb-6" data-testid="about-intro">
                As a business strategist, I specialize in helping visionaries, struggling business owners, and aspiring 
                entrepreneurs <strong>jumpstart their business</strong> and create <strong>additional 
                income streams</strong> outside of their 9-to-5.
              </p>
              <p className="body-text text-[#475569] mb-8">
                I don't just teach strategy. I live it. With over 10 active businesses across multiple 
                industries, I bring real-world experience and practical wisdom to every consultation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/schedule" 
                  className="btn-primary-orange inline-flex items-center justify-center gap-2"
                  data-testid="about-cta-primary"
                >
                  Book a Free 20 Min Business Jumpstart Session
                  <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/brands" 
                  className="btn-outline inline-flex items-center justify-center gap-2"
                  data-testid="about-cta-secondary"
                >
                  See My Portfolio
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={ABOUT_IMAGE}
                alt="David Ashley"
                className="rounded-lg shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#1D4ED8] text-white p-6 max-w-[200px]">
                <div className="text-3xl font-bold mb-1">15+</div>
                <div className="text-sm opacity-80">Years of Business Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="trust-strip py-12" data-testid="about-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-[#EFF6FF] rounded-full flex items-center justify-center mx-auto mb-3">
                <Building size={28} className="text-[#1D4ED8]" />
              </div>
              <div className="heading-2 text-3xl text-[#0A0F1C] mb-1">10+</div>
              <div className="text-sm text-[#475569]">Active Businesses</div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FFF7ED] rounded-full flex items-center justify-center mx-auto mb-3">
                <Award size={28} className="text-[#F97316]" />
              </div>
              <div className="heading-2 text-3xl text-[#0A0F1C] mb-1">15+</div>
              <div className="text-sm text-[#475569]">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#EFF6FF] rounded-full flex items-center justify-center mx-auto mb-3">
                <Users size={28} className="text-[#1D4ED8]" />
              </div>
              <div className="heading-2 text-3xl text-[#0A0F1C] mb-1">100+</div>
              <div className="text-sm text-[#475569]">Entrepreneurs Helped</div>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-[#FFF7ED] rounded-full flex items-center justify-center mx-auto mb-3">
                <Target size={28} className="text-[#F97316]" />
              </div>
              <div className="heading-2 text-3xl text-[#0A0F1C] mb-1">7</div>
              <div className="text-sm text-[#475569]">Stage Framework</div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="section-padding bg-white" data-testid="about-story">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <span className="overline mb-4 inline-block">My Story</span>
            <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-8">
              Building a Business Empire While Working a 9-to-5: Creating Side Hustles and Additional Income Streams
            </h2>
            
            <div className="prose prose-lg max-w-none">
              <p className="text-[#475569] mb-6 leading-relaxed">
                I know what it feels like to sit at a 9-to-5 with a vision burning inside you. The pull
                of <strong>something bigger</strong>: a business of your own, a real legacy for your family,
                freedom on your terms. I lived in that tension for years. The difference between dreamers
                and doers is rarely talent. It is the willingness to build a <strong>plan</strong>, take the
                first imperfect step, and refuse to quit until the vision becomes a verified business.
              </p>
              <p className="text-[#475569] mb-6 leading-relaxed">
                I bet on myself when no one else did, and the results compounded. Through years of trial,
                error, late nights, and hard-won wins, I developed a systematic approach to starting,
                stabilizing, and scaling businesses. That blueprint became my <strong>7-Stage Business
                Jumpstart Framework</strong>. This is the same system I now use to coach entrepreneurs from
                idea to incorporated, funded, and operating.
              </p>
              <p className="text-[#475569] mb-6 leading-relaxed">
                Today, my portfolio spans health and wellness, workforce development, life and purpose
                coaching, local fresh produce operations, insurance, and more. Beyond business, I serve
                as an Assistant Pastor at my church and founded a nonprofit organization, because true
                success is measured by impact, not just income.
              </p>
              <p className="text-[#475569] leading-relaxed">
                If you have a vision that will not let you sleep, or a business that is stuck, I am here
                to help you launch it, structure it, and grow it the right way. You do not need permission
                to start. You need a strategist in your corner, and a plan that actually works.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="about-highlights">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="overline mb-4 inline-block">Credentials</span>
              <h2 className="heading-2 text-3xl lg:text-4xl text-[#0A0F1C] mb-8">
                A Multi-Faceted Leader
              </h2>
              <div className="space-y-4">
                {highlights.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-3"
                    data-testid={`highlight-${index}`}
                  >
                    <CheckCircle size={20} className="text-[#1D4ED8] flex-shrink-0" />
                    <span className="text-[#0A0F1C] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white p-6 border border-[#E2E8F0] rounded-lg shadow-sm"
                    data-testid={`value-${index}`}
                  >
                    <Icon size={28} className={`${index % 2 === 0 ? 'text-[#1D4ED8]' : 'text-[#F97316]'} mb-3`} />
                    <h3 className="font-bold text-[#0A0F1C] mb-2">{value.title}</h3>
                    <p className="text-sm text-[#475569]">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-padding bg-white" data-testid="about-contact">
        <div className="container-custom">
          <div className="bg-[#0F172A] p-8 lg:p-12 text-center">
            <h2 className="heading-2 text-3xl text-white mb-4">Let's Work Together</h2>
            <p className="text-[#94A3B8] mb-6 max-w-xl mx-auto">
              Ready to jumpstart your business? I'd love to hear about your goals and see how I can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link 
                to="/schedule" 
                className="btn-primary-orange inline-flex items-center justify-center gap-2"
                data-testid="about-contact-cta-primary"
              >
                FREE 20-Minute Business Jumpstart Session
                <ArrowRight size={18} />
              </Link>
              <Link 
                to="/contact" 
                className="btn-outline border-white text-white hover:bg-white/10 inline-flex items-center justify-center gap-2"
                data-testid="about-contact-cta-secondary"
              >
                Send a Message
              </Link>
            </div>
            <div className="text-[#64748B] text-sm">
              <p>info@DCAEnterprisesInc.com • 912-376-9006</p>
              <p className="mt-1">10520 Abercorn St Ste D, Savannah, GA 31419</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
